import { useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  OutlinedInput,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import AddAttributeDialog from './AddAttributeDialog';
import { useTranslation } from '../../common/components/LocalizationProvider';
import { useAttributePreference } from '../../common/util/preferences';
import {
  distanceFromMeters, distanceToMeters, distanceUnitString, speedFromKnots, speedToKnots, speedUnitString, volumeFromLiters, volumeToLiters, volumeUnitString,
} from '../../common/util/converter';
import useFeatures from '../../common/util/useFeatures';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useSuperAdmin } from '../../common/util/permissions';

// 2ECOMP: Prefix for internal feature-gating attributes (hidden from non-superadmin)
const INTERNAL_ATTR_PREFIX = 'feature.';

// Exported pure helper so it can be unit tested without rendering the component
export const convertToList = (attributes, definitions = {}, superAdmin = false) => {
  const booleanList = [];
  const otherList = [];
  const excludeAttributes = ['speedUnit', 'distanceUnit', 'altitudeUnit', 'volumeUnit', 'timezone'];
  const getAttributeType = (value) => {
    if (typeof value === 'number') {
      return 'number';
    } if (typeof value === 'boolean') {
      return 'boolean';
    }
    return 'string';
  };
  const getAttributeSubtype = (key) => {
    const definition = definitions[key];
    return definition && definition.subtype;
  };

  Object.keys(attributes || {}).filter((key) => !excludeAttributes.includes(key))
    .filter((key) => superAdmin || !key.startsWith(INTERNAL_ATTR_PREFIX))
    .forEach((key) => {
    const value = attributes[key];

    // Ignore nested objects (for example the grouped `form` object)
    // They should be preserved in attributes but not rendered as a separate phantom entry like "[object Object]"
    if (value !== null && typeof value === 'object') {
      return;
    }

    const type = getAttributeType(value);
    const subtype = getAttributeSubtype(key);
    if (type === 'boolean') {
      booleanList.push({ key, value, type, subtype });
    } else {
      otherList.push({ key, value, type, subtype });
    }
  });
  return [...otherList, ...booleanList];
};

const EditAttributesAccordion = ({ attribute, attributes, setAttributes, definitions, focusAttribute, disabled }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const features = useFeatures();
  const superAdmin = useSuperAdmin();

  const speedUnit = useAttributePreference('speedUnit');
  const distanceUnit = useAttributePreference('distanceUnit');
  const volumeUnit = useAttributePreference('volumeUnit');

  const [addDialogShown, setAddDialogShown] = useState(false);

  const updateAttribute = (key, value, type, subtype) => {
    const updatedAttributes = { ...attributes };
    switch (subtype) {
      case 'speed':
        updatedAttributes[key] = speedToKnots(Number(value), speedUnit);
        break;
      case 'distance':
        updatedAttributes[key] = distanceToMeters(Number(value), distanceUnit);
        break;
      case 'volume':
        updatedAttributes[key] = volumeToLiters(Number(value), volumeUnit);
        break;
      default:
        updatedAttributes[key] = type === 'number' ? Number(value) : value;
        break;
    }
    setAttributes(updatedAttributes);
  };

  const deleteAttribute = (key) => {
    // 2ECOMP: Prevent non-superadmin from deleting internal attributes
    if (!superAdmin && key.startsWith(INTERNAL_ATTR_PREFIX)) return;
    const updatedAttributes = { ...attributes };
    delete updatedAttributes[key];
    setAttributes(updatedAttributes);
  };

  const getAttributeName = (key, subtype) => {
    const definition = definitions[key];
    const name = definition ? definition.name : key;
    switch (subtype) {
      case 'speed':
        return `${name} (${speedUnitString(speedUnit, t)})`;
      case 'distance':
        return `${name} (${distanceUnitString(distanceUnit, t)})`;
      case 'volume':
        return `${name} (${volumeUnitString(volumeUnit, t)})`;
      default:
        return name;
    }
  };

  const getDisplayValue = (value, subtype) => {
    if (value) {
      switch (subtype) {
        case 'speed':
          return speedFromKnots(value, speedUnit);
        case 'distance':
          return distanceFromMeters(value, distanceUnit);
        case 'volume':
          return volumeFromLiters(value, volumeUnit);
        default:
          return value;
      }
    }
    return '';
  };

  const handleAddResult = (definition) => {
    setAddDialogShown(false);
    if (definition) {
      switch (definition.type) {
        case 'number':
          updateAttribute(definition.key, 0);
          break;
        case 'boolean':
          updateAttribute(definition.key, false);
          break;
        default:
          updateAttribute(definition.key, '');
          break;
      }
    }
  };

  if (features.disableAttributes) {
    return null;
  }

  const attributesList = convertToList(attributes, definitions, superAdmin);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography
          variant="overline"
          sx={{
            color: tokens.text.muted,
            fontWeight: 600,
            letterSpacing: 1,
          }}
        >
          {t('sharedAttributes')} ({attributesList.length})
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setAddDialogShown(true)}
          startIcon={<AddIcon />}
          disabled={disabled}
          sx={{
            borderColor: tokens.accent.primary,
            color: tokens.accent.primary,
            borderRadius: tokens.radius.md,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: tokens.accent.subtle,
              borderColor: tokens.accent.primary,
            },
          }}
        >
          {t('sharedAdd')}
        </Button>
      </Box>

      {/* Empty state */}
      {attributesList.length === 0 && (
        <Box
          sx={{
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            backgroundColor: tokens.surface.glass,
            borderRadius: tokens.radius.lg,
            border: `1px dashed ${tokens.border.default}`,
          }}
        >
          <Typography variant="body2" sx={{ color: tokens.text.muted }}>
            {t('sharedNoAttributes') || 'Nenhum atributo configurado'}
          </Typography>
          <Typography variant="caption" sx={{ color: tokens.text.muted }}>
            {t('sharedAddAttributeHelp') || 'Clique em "Adicionar" para configurar atributos personalizados'}
          </Typography>
        </Box>
      )}

      {/* Attributes list */}
      {attributesList.map(({ key, value, type, subtype }) => {
        if (type === 'boolean') {
          return (
            <Box
              key={key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1,
                px: 2,
                backgroundColor: tokens.surface.glass,
                borderRadius: tokens.radius.md,
                border: `1px solid ${tokens.border.subtle}`,
                transition: tokens.transitions.all,
                '&:hover': {
                  borderColor: tokens.border.default,
                  backgroundColor: tokens.surface.elevated,
                },
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value}
                    onChange={(e) => updateAttribute(key, e.target.checked)}
                    disabled={disabled}
                    sx={{
                      color: tokens.text.secondary,
                      '&.Mui-checked': {
                        color: tokens.accent.primary,
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: tokens.text.primary }}>
                    {getAttributeName(key, subtype)}
                  </Typography>
                }
              />
              <IconButton
                size="small"
                onClick={() => deleteAttribute(key)}
                disabled={disabled}
                sx={{
                  color: tokens.text.muted,
                  '&:hover': {
                    color: tokens.semantic.error,
                    backgroundColor: tokens.semantic.errorBg,
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        }

        return (
          <FormControl key={key} fullWidth size="small" className="premium-input">
            <InputLabel>{getAttributeName(key, subtype)}</InputLabel>
            <OutlinedInput
              label={getAttributeName(key, subtype)}
              type={type === 'number' ? 'number' : 'text'}
              value={getDisplayValue(value, subtype)}
              onChange={(e) => updateAttribute(key, e.target.value, type, subtype)}
              autoFocus={focusAttribute === key}
              disabled={disabled}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={() => deleteAttribute(key)}
                    disabled={disabled}
                    sx={{
                      color: tokens.text.muted,
                      '&:hover': {
                        color: tokens.semantic.error,
                        backgroundColor: tokens.semantic.errorBg,
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
        );
      })}

      <AddAttributeDialog
        open={addDialogShown}
        onResult={handleAddResult}
        definitions={definitions}
      />
    </Box>
  );
};

export default EditAttributesAccordion;
